import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ExternalLink } from "lucide-react";

function SourceLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
    >
      {children}
      <ExternalLink className="h-3.5 w-3.5" />
    </a>
  );
}

export default function SellerMeatGuidance() {
  const [, setLocation] = useLocation();

  return (
    <div className="container mx-auto max-w-2xl px-4 py-6 space-y-6">
      <button
        type="button"
        onClick={() => setLocation("/seller/compliance")}
        className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>

      <div>
        <h1 className="font-display text-2xl font-bold text-foreground">
          Selling meat &amp; poultry
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Meat is the most tightly regulated thing you can sell. The short
          version below is here so you can sell legally without guessing. It's a
          summary, not legal advice.
        </p>
      </div>

      <section className="space-y-2">
        <h2 className="font-semibold text-foreground">Poultry &amp; eggs</h2>
        <p className="text-sm text-muted-foreground">
          Poultry has limited on-farm exemptions. If you raise and slaughter
          under 1,000 birds a year on your own farm, you can sell them directly
          without a federal inspector on site, as long as you meet the sanitary
          and labeling rules. Eggs have their own handling and labeling rules.
          Check the specifics before you list.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold text-foreground">
          Red meat (pork, beef, lamb, goat)
        </h2>
        <p className="text-sm text-muted-foreground">
          To sell red meat <span className="font-medium text-foreground">as cuts</span>{" "}
          (chops, bacon, sausage, packaged by the pound), the animal must be
          slaughtered and processed at a{" "}
          <span className="font-medium text-foreground">USDA-inspected facility</span>.
          Unlike poultry, there is no small-farm exemption for red meat. New
          York's state license (Article 5-A) only covers non-amenable species
          like bison, rabbit, and farmed venison, not pork or beef.
        </p>
      </section>

      <section className="space-y-2 rounded-2xl border border-brand-secondary/30 bg-brand-secondary/5 p-4">
        <h2 className="font-semibold text-foreground">
          The legal way to sell without USDA cuts: sell a share of a live animal
        </h2>
        <p className="text-sm text-muted-foreground">
          You can sell a live animal, or a whole / half / quarter share of one,
          as long as the sale happens{" "}
          <span className="font-medium text-foreground">before slaughter</span>.
          The buyer legally owns the animal, it's processed "custom exempt," and
          the meat comes back marked{" "}
          <span className="font-medium text-foreground">Not for Sale</span> for
          that owner's household. You can still coordinate the processing and
          hand over the finished, cut, wrapped meat.
        </p>
        <p className="text-sm text-muted-foreground">
          What's <span className="font-medium text-foreground">not</span> allowed:
          slaughtering first, filling a freezer with cuts, then selling those cuts
          as "shares." Ownership has to transfer while the animal is alive.
          Keeping it to a few owners per animal (whole, half, quarter) keeps you
          clearly inside the exemption; slicing one animal into many small shares
          starts to look like retailing uninspected meat.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold text-foreground">Where to get real answers</h2>
        <p className="text-sm text-muted-foreground">
          These are free and will confirm your specific setup:
        </p>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li>
            <SourceLink href="https://smallfarms.cornell.edu/resources/guide-to-direct-marketing-livestock-and-poultry-2/usda-food-safety-and-inspection-service-fsis/">
              Cornell Small Farms: Guide to Direct Marketing Livestock &amp; Poultry
            </SourceLink>
          </li>
          <li>
            <SourceLink href="https://agriculture.ny.gov/animals/livestock-poultry">
              NY Dept. of Agriculture &amp; Markets: Livestock &amp; Poultry
            </SourceLink>
          </li>
          <li>
            <SourceLink href="https://www.ccelivestock.com/news-updates-recent-publications/understanding-slaughter-and-butcher-options">
              Cornell Cooperative Extension: Slaughter &amp; butcher options in NYS
            </SourceLink>
          </li>
        </ul>
      </section>

      <p className="text-xs text-muted-foreground">
        Stoop is not your legal advisor. You're responsible for knowing and
        following the laws that apply to what you sell.
      </p>

      <div className="flex justify-end">
        <Button type="button" variant="outline" onClick={() => setLocation("/seller/compliance")}>
          Back to attestation
        </Button>
      </div>
    </div>
  );
}
